import { useState } from "react";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Popover,
  Button,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteIcon from "@mui/icons-material/Delete";
import _ from "lodash";
import { useDictionary } from "./useDictionary";

interface TypeItem {
  Name?: string;
  TypeId?: number;
  [key: string]: unknown;
}

export const PopoverTypeList: React.FC<{
  onHide: () => void;
  groupTypes: TypeItem[];
  onToggle: (groupType: TypeItem) => void;
  onRefresh?: () => void;
  onClear?: () => void;
}> = ({ onHide, groupTypes, onToggle, onRefresh, onClear }) => {
  return (
    <List
      dense
      subheader={<ListSubheader>Select Group Types:</ListSubheader>}
      sx={{ minWidth: 220 }}
    >
      {groupTypes &&
        groupTypes.map((groupType, i: number) => (
          <ListItemButton
            key={i}
            onClick={() => onToggle(groupType)}
            divider
          >
            <ListItemText primary={groupType.Name} />
          </ListItemButton>
        ))}
      <ListItem>
        <Stack direction="row" spacing={1} sx={{ ml: "auto" }}>
          <Button size="small" startIcon={<DeleteIcon />} onClick={onClear}>
            Clear
          </Button>
          <Button size="small" startIcon={<RefreshIcon />} onClick={onRefresh}>
            Refresh
          </Button>
          <Button size="small" startIcon={<CloseIcon />} onClick={onHide}>
            Close
          </Button>
        </Stack>
      </ListItem>
    </List>
  );
};

/**
 * Hook to select group types
 */
// eslint-disable-next-line react-refresh/only-export-components -- hook is co-located with its popover component
export function useSelectTypePopover() {
  const { dictionary, refreshDictionary } = useDictionary("GroupTypes");

  const [selectedTypes, setSelectedTypes] = useState<TypeItem[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_allTypes, setAllTypes] = useState(true);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const clearTypeFilter = (clear: boolean) => {
    setSelectedTypes([]);
    setAllTypes(clear);
  };

  const toggleTypeFilter = (checked: boolean, groupType: TypeItem) => {
    setAllTypes(false);
    let newSelection = [...selectedTypes];
    if (checked) {
      newSelection = _.union([groupType], newSelection);
    } else {
      newSelection = _.without(newSelection, groupType);
    }
    setSelectedTypes(newSelection);
  };

  const handleToggleType = (groupType: TypeItem) => {
    toggleTypeFilter(true, groupType);
  };

  const removeType = (groupType: TypeItem) => {
    toggleTypeFilter(false, groupType);
  };

  const presentTypes = (opts: { event: React.MouseEvent | Event }) => {
    const target = opts.event?.target || (opts.event as React.MouseEvent)?.currentTarget || null;
    setAnchorEl(target as HTMLElement | null);
  };

  const dismissTypes = () => {
    setAnchorEl(null);
  };

  const TypesPopover = (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={dismissTypes}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      sx={{ zIndex: 30000 }}
    >
      <PopoverTypeList
        onHide={dismissTypes}
        groupTypes={dictionary as TypeItem[]}
        onToggle={handleToggleType}
        onRefresh={refreshDictionary}
        onClear={() => clearTypeFilter(true)}
      />
    </Popover>
  );

  return {
    presentTypes,
    dismissTypes,
    clearTypeFilter,
    toggleTypeFilter,
    removeType,
    handleToggleType,
    selectedTypes,
    PopoverTypeList,
    groupTypes: dictionary,
    TypesPopover,
  };
}
